import type { Grade7, Severity, TemplateParamCatalogOutput, TemplateVersion } from 'used-vehicle-contracts';
import { FormField } from '../../components/ui/FormField';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { formatMoney } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { GRADE7_VALUES, hasHybridSystem, SEVERITIES, toOptionalNumber } from '../estimates/estimates-display';

/** Everything the form holds, as strings, because that is what a `<select>` and a `<textarea>` hold. */
export interface InspectionFieldValues {
  grade7: Grade7;
  interior: Severity;
  engine: Severity;
  hybrid: Severity;
  tires: Severity;
  damageGroup1Index: string;
  damageGroup2Index: string;
  inspectorNotes: string;
}

/** The patch that goes to `inspections.update` — one field at a time, as it is changed. */
export interface InspectionPatchValues {
  grade7?: Grade7;
  interior?: Severity;
  engine?: Severity;
  hybrid?: Severity;
  tires?: Severity;
  damageGroup1Index?: number | null;
  damageGroup2Index?: number | null;
  inspectorNotes?: string | null;
}

export interface InspectionFieldsProps {
  values: InspectionFieldValues;
  /** A petrol vehicle has no hybrid system to assess, so the select is replaced by a line (PP §7.5). */
  fuel: string | null | undefined;
  damageOptions: TemplateParamCatalogOutput | null;
  templateValues: Pick<TemplateVersion, 'damageGroup1' | 'damageGroup2'> | null;
  disabled?: boolean;
  onChange: (form: Partial<InspectionFieldValues>, patch: InspectionPatchValues) => void;
}

/**
 * The manual inspection, as a set of fields — **one form, two owners** (plan §7).
 *
 * The estimator's step 2 and the stock vehicle's Inspection tab are the same assessment of the same car
 * at two moments, and the purchase copies the row from one to the other. Writing the form twice would
 * mean two places where "unknown" could accidentally become "excellent"; there is one.
 *
 * Every selection starts **Unknown**, and unknown is never read as Excellent: the appraisal refuses to
 * price an unrecorded severity and the card beside this one says "incomplete" rather than a number
 * somebody might write down (PP AC03).
 */
export const InspectionFields = ({ values, fuel, damageOptions, templateValues, disabled = false, onChange }: InspectionFieldsProps) => {
  const { t } = useI18nTranslation();
  const hybridApplies = hasHybridSystem(fuel);

  const severityField = (key: 'interior' | 'engine' | 'hybrid' | 'tires', label: string) => (
    <FormField
      id={`inspection-${key}`}
      label={label}
    >
      <Select
        id={`inspection-${key}`}
        value={values[key]}
        disabled={disabled}
        onChange={(event) => onChange({ [key]: event.target.value as Severity }, { [key]: event.target.value as Severity })}
        options={SEVERITIES.map((severity) => ({ value: severity, label: t(`enums.severity.${severity}`) }))}
      />
    </FormField>
  );

  const damageField = (group: 1 | 2) => {
    const key = group === 1 ? 'damageGroup1Index' : 'damageGroup2Index';
    const options = (group === 1 ? damageOptions?.damageGroup1 : damageOptions?.damageGroup2) ?? [];
    const amounts = (group === 1 ? templateValues?.damageGroup1 : templateValues?.damageGroup2) ?? [];

    return (
      <FormField
        id={`inspection-damage-${group}`}
        label={t(`estimates.damageGroup${group}`)}
        hint={t('estimates.damageGroupHint')}
      >
        <Select
          id={`inspection-damage-${group}`}
          value={values[key]}
          disabled={disabled}
          placeholder={t('estimates.notAnswered')}
          onChange={(event) => onChange({ [key]: event.target.value }, { [key]: toOptionalNumber(event.target.value) })}
          options={options.map((option) => ({
            value: String(option.index),
            label: `${option.arabic} — ${option.english}${amounts[option.index] === undefined ? '' : ` · ${formatMoney(amounts[option.index])}`}`,
          }))}
        />
      </FormField>
    );
  };

  return (
    <div className="space-y-4">
      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-card-foreground">{t('estimates.conditionSection')}</h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            id="inspection-grade7"
            label={t('estimates.grade7')}
            hint={t('estimates.grade7Hint')}
          >
            <Select
              id="inspection-grade7"
              value={values.grade7}
              disabled={disabled}
              onChange={(event) => onChange({ grade7: event.target.value as Grade7 }, { grade7: event.target.value as Grade7 })}
              options={GRADE7_VALUES.map((grade) => ({ value: grade, label: t(`enums.grade7.${grade}`) }))}
            />
          </FormField>
          {severityField('interior', t('estimates.interior'))}
          {severityField('engine', t('estimates.engine'))}
          {hybridApplies ? (
            severityField('hybrid', t('estimates.hybrid'))
          ) : (
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-card-foreground">{t('estimates.hybrid')}</p>
              <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">{t('estimates.hybridNotApplicable')}</p>
            </div>
          )}
          {severityField('tires', t('estimates.tires'))}
        </div>

        <FormField
          id="inspection-notes"
          label={t('estimates.inspectorNotes')}
        >
          <Textarea
            id="inspection-notes"
            rows={3}
            disabled={disabled}
            value={values.inspectorNotes}
            onChange={(event) =>
              onChange({ inspectorNotes: event.target.value }, { inspectorNotes: event.target.value.trim() === '' ? null : event.target.value })
            }
          />
        </FormField>
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-card-foreground">{t('estimates.structuralSection')}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {damageField(1)}
          {damageField(2)}
        </div>
      </section>
    </div>
  );
};
